import { useCallback, useMemo, useRef, useState } from 'react';

import type { Media } from '@nucleus-commerce/api-client/admin';

import { toPutBody, type GalleryItem } from '../components/product-media-gallery';

import type { ProductGalleryDraft } from './use-product-gallery-draft';
import { useAttachProductMedia } from './use-product-media';

export interface NewProductGallery extends ProductGalleryDraft {
  productId: null;
  /** Resolves once no file is still on its way, so a save doesn't leave one behind. */
  uploadsSettled: () => Promise<void>;
  /** Maps the files onto the product that was just created. Resolves false if that failed. */
  attach: (productId: string) => Promise<boolean>;
}

/**
 * The gallery of a product that doesn't exist yet. Uploads go to the media library at once, as on a
 * saved product; which of them belong to the product is held here until the create page's save
 * makes the product and maps them. The form owns the save, so this registers no save bar entry of
 * its own: the page hands `dirty` and `discard` to the form's (ADR 105).
 */
export function useNewProductGallery(): NewProductGallery {
  const attachMedia = useAttachProductMedia();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const latest = useRef<GalleryItem[]>(items);

  const change = useCallback((next: GalleryItem[]) => {
    latest.current = next;
    setItems(next);
  }, []);

  const add = useCallback(
    (media: Media[]) => {
      const known = new Set(latest.current.map((item) => item.mediaId));
      const fresh = media
        .filter((m) => !known.has(m.id))
        .map((m) => ({ mediaId: m.id, variantIds: [], media: m }));
      if (fresh.length > 0) change([...latest.current, ...fresh]);
    },
    [change],
  );

  const refreshMedia = useCallback(
    (media: Media) =>
      change(latest.current.map((item) => (item.mediaId === media.id ? { ...item, media } : item))),
    [change],
  );

  const discard = useCallback(() => change([]), [change]);

  const activeUploads = useRef(0);
  const waiting = useRef<Array<() => void>>([]);
  const onUploadsChange = useCallback((active: number) => {
    activeUploads.current = active;
    setUploading(active > 0);
    if (active === 0) for (const resolve of waiting.current.splice(0)) resolve();
  }, []);
  const uploadsSettled = useCallback(
    () =>
      activeUploads.current === 0
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            waiting.current.push(resolve);
          }),
    [],
  );

  const attach = useCallback(
    async (productId: string) => {
      if (latest.current.length === 0) return true;
      try {
        await attachMedia.mutateAsync({ productId, body: toPutBody(latest.current) });
        return true;
      } catch {
        return false;
      }
    },
    [attachMedia],
  );

  // Nothing to load for a product that doesn't exist.
  const query = useMemo(
    () => ({ isPending: false, error: null, refetch: () => Promise.resolve(null) }),
    [],
  );

  return {
    productId: null,
    query,
    items,
    latest,
    change,
    add,
    refreshMedia,
    onUploadsChange,
    dirty: items.length > 0 || uploading,
    saving: attachMedia.isPending,
    save: () => Promise.resolve(),
    discard,
    uploadsSettled,
    attach,
  };
}
